import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MEDIA_LIMITS } from "./mediaLimits";

/**
 * Photo editor for the Create post composer — crop, rotate, flip, light
 * adjustments and filter presets, the way Facebook lets you touch up a photo
 * before posting.
 *
 * Everything happens in the browser on a canvas; nothing is uploaded until the
 * post is published. The preview runs on a downscaled copy with a CSS filter so
 * dragging stays smooth; Save re-renders from the full-size original and bakes
 * the same filter into the pixels (see applyAdjust), so what you see is what is
 * posted.
 *
 * Props: `file` (the picked image), `primary` (brand colour), `onCancel()`,
 * `onSave(file)` with the edited File.
 */

const ASPECTS = [
  { key: "free", label: "Free" },
  { key: "orig", label: "Original" },
  { key: "1:1", label: "Square", ratio: 1 },
  { key: "4:5", label: "Portrait", ratio: 4 / 5 },
  { key: "16:9", label: "Wide", ratio: 16 / 9 },
];

const DEFAULT_ADJ = { brightness: 1, contrast: 1, saturate: 1, sepia: 0 };
const SLIDERS = [
  { key: "brightness", label: "Brightness", min: 0.5, max: 1.5, step: 0.01 },
  { key: "contrast", label: "Contrast", min: 0.5, max: 1.5, step: 0.01 },
  { key: "saturate", label: "Saturation", min: 0, max: 2, step: 0.01 },
  { key: "sepia", label: "Warmth", min: 0, max: 1, step: 0.01 },
];
const FILTERS = [
  { key: "none", label: "Original", v: {} },
  { key: "vivid", label: "Vivid", v: { contrast: 1.12, saturate: 1.45 } },
  { key: "bright", label: "Bright", v: { brightness: 1.15, contrast: 1.05 } },
  { key: "warm", label: "Warm", v: { sepia: 0.3, saturate: 1.2, brightness: 1.05 } },
  { key: "fade", label: "Fade", v: { contrast: 0.82, brightness: 1.08, saturate: 0.85 } },
  { key: "mono", label: "Mono", v: { saturate: 0 } },
  { key: "noir", label: "Noir", v: { saturate: 0, contrast: 1.35, brightness: 0.95 } },
  { key: "vintage", label: "Vintage", v: { sepia: 0.55, contrast: 0.92, brightness: 1.05 } },
];

const PREVIEW_MAX = 1600; // long side of the on-screen working copy
const EXPORT_MAX = 4096; // long side of the saved image
const MIN_CROP = 36; // px on screen

const FULL = { x: 0, y: 0, w: 1, h: 1 };
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const sameAdj = (a, b) => Object.keys(DEFAULT_ADJ).every((k) => Math.abs(a[k] - b[k]) < 0.001);

const cssFilter = (a) =>
  `brightness(${a.brightness}) contrast(${a.contrast}) saturate(${a.saturate}) sepia(${a.sepia})`;

/**
 * The CSS filter functions above, applied to pixels in the same order with the
 * spec's formulas, so the saved image matches the preview in every browser
 * (canvas `ctx.filter` is missing in older Safari).
 */
function applyAdjust(data, a) {
  const b = a.brightness;
  const c = a.contrast;
  const ci = 0.5 * (1 - c) * 255;
  const s = a.saturate;
  const p = 1 - a.sepia;
  // saturate matrix
  const s00 = 0.213 + 0.787 * s, s01 = 0.715 - 0.715 * s, s02 = 0.072 - 0.072 * s;
  const s10 = 0.213 - 0.213 * s, s11 = 0.715 + 0.285 * s, s12 = 0.072 - 0.072 * s;
  const s20 = 0.213 - 0.213 * s, s21 = 0.715 - 0.715 * s, s22 = 0.072 + 0.928 * s;
  // sepia matrix
  const p00 = 0.393 + 0.607 * p, p01 = 0.769 - 0.769 * p, p02 = 0.189 - 0.189 * p;
  const p10 = 0.349 - 0.349 * p, p11 = 0.686 + 0.314 * p, p12 = 0.168 - 0.168 * p;
  const p20 = 0.272 - 0.272 * p, p21 = 0.534 - 0.534 * p, p22 = 0.131 + 0.869 * p;
  const cl = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);
  for (let i = 0; i < data.length; i += 4) {
    let r = cl(data[i] * b);
    let g = cl(data[i + 1] * b);
    let bl = cl(data[i + 2] * b);
    r = cl(r * c + ci);
    g = cl(g * c + ci);
    bl = cl(bl * c + ci);
    let r2 = cl(s00 * r + s01 * g + s02 * bl);
    let g2 = cl(s10 * r + s11 * g + s12 * bl);
    let b2 = cl(s20 * r + s21 * g + s22 * bl);
    data[i] = cl(p00 * r2 + p01 * g2 + p02 * b2);
    data[i + 1] = cl(p10 * r2 + p11 * g2 + p12 * b2);
    data[i + 2] = cl(p20 * r2 + p21 * g2 + p22 * b2);
  }
}

/** The image turned and mirrored as the member set it, long side ≤ maxSide. */
function drawOriented(img, rot, flipH, flipV, maxSide) {
  const sw = img.naturalWidth;
  const sh = img.naturalHeight;
  const scale = Math.min(1, maxSide / Math.max(sw, sh));
  const w = Math.max(1, Math.round(sw * scale));
  const h = Math.max(1, Math.round(sh * scale));
  const swap = rot % 180 !== 0;
  const c = document.createElement("canvas");
  c.width = swap ? h : w;
  c.height = swap ? w : h;
  const ctx = c.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.translate(c.width / 2, c.height / 2);
  // Flip before rotate in the transform list = flip along the axes the member
  // is looking at, whatever the rotation.
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  return c;
}

/** Largest centred rect of display-ratio `r` inside a W×H image, normalised. */
function fitAspect(r, W, H) {
  if (!r || !W || !H) return FULL;
  let w = W;
  let h = W / r;
  if (h > H) {
    h = H;
    w = H * r;
  }
  return { x: (W - w) / 2 / W, y: (H - h) / 2 / H, w: w / W, h: h / H };
}

const toBlob = (canvas, type, q) => new Promise((res) => canvas.toBlob(res, type, q));

const Icon = ({ d, className = "h-[18px] w-[18px]", sw = 1.8 }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={sw} aria-hidden="true">
    {[].concat(d).map((p, i) => (
      <path key={i} strokeLinecap="round" strokeLinejoin="round" d={p} />
    ))}
  </svg>
);
const ICONS = {
  edit: ["M15.2 5.2l3.6 3.6M4 20l4.3-.9L19.4 8a2.5 2.5 0 00-3.6-3.6L4.9 15.7 4 20z"],
  crop: ["M6 2v14a2 2 0 002 2h14", "M18 22V8a2 2 0 00-2-2H2"],
  adjust: ["M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0", "M16 4v4M10 10v4M18 16v4"],
  filters: ["M12 3a6 6 0 100 12 6 6 0 000-12z", "M8 9a6 6 0 108 8", "M16 9a6 6 0 11-8 8"],
  rotL: ["M3 8V3m0 5h5", "M3.5 8A9 9 0 1112 21"],
  rotR: ["M21 8V3m0 5h-5", "M20.5 8A9 9 0 1012 21"],
  flipH: ["M12 3v18", "M8 7L3 12l5 5V7z", "M16 7l5 5-5 5V7z"],
  flipV: ["M3 12h18", "M7 8l5-5 5 5H7z", "M7 16l5 5 5-5H7z"],
  reset: ["M4 4v5h5", "M5.1 15a7 7 0 101.6-7.3L4 9"],
  close: ["M6 18L18 6M6 6l12 12"],
  check: ["M5 13l4 4L19 7"],
};

export default function ImageEditor({ file, primary = "#6366f1", onCancel, onSave }) {
  const [img, setImg] = useState(null);
  const [tab, setTab] = useState("crop");
  const [rot, setRot] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [aspect, setAspect] = useState("free");
  const [crop, setCrop] = useState(FULL);
  const [adj, setAdj] = useState(DEFAULT_ADJ);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const drag = useRef(null);

  // Load the picked file.
  useEffect(() => {
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => setImg(im);
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Esc = Cancel (the composer behind has no Esc close, so nothing else reacts).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  // The working copy, re-cut whenever the orientation changes.
  const oriented = useMemo(
    () => (img ? drawOriented(img, rot, flipH, flipV, PREVIEW_MAX) : null),
    [img, rot, flipH, flipV]
  );

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setStage({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // On-screen size of the image: fit inside the stage with some breathing room.
  const disp = useMemo(() => {
    if (!oriented || !stage.w) return { w: 0, h: 0 };
    const pad = 40;
    const s = Math.min((stage.w - pad) / oriented.width, (stage.h - pad) / oriented.height);
    return { w: Math.round(oriented.width * s), h: Math.round(oriented.height * s) };
  }, [oriented, stage]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !oriented) return;
    c.width = oriented.width;
    c.height = oriented.height;
    c.getContext("2d").drawImage(oriented, 0, 0);
  }, [oriented]);

  const ratioOf = useCallback(
    (key) => {
      const a = ASPECTS.find((x) => x.key === key);
      if (!a || key === "free") return null;
      if (key === "orig") return oriented ? oriented.width / oriented.height : null;
      return a.ratio;
    },
    [oriented]
  );
  const ratio = ratioOf(aspect);

  const pickAspect = (key) => {
    setAspect(key);
    const r = ratioOf(key);
    setCrop(r ? fitAspect(r, oriented.width, oriented.height) : FULL);
  };

  // Turning the photo turns the crop with it; a fixed shape (4:5 → 5:4) is
  // re-fitted instead so the chosen shape stays what it says.
  const rotate = (dir) => {
    const next = (rot + (dir > 0 ? 90 : 270)) % 360;
    setRot(next);
    if (aspect === "free") {
      setCrop((c) => (dir > 0 ? { x: 1 - c.y - c.h, y: c.x, w: c.h, h: c.w } : { x: c.y, y: 1 - c.x - c.w, w: c.h, h: c.w }));
    } else if (aspect === "orig") {
      setCrop(FULL);
    } else if (oriented) {
      // oriented still has the old dimensions here — swap them.
      setCrop(fitAspect(ratio, oriented.height, oriented.width));
    }
  };
  const flip = (axis) => {
    if (axis === "h") {
      setFlipH((v) => !v);
      setCrop((c) => ({ ...c, x: 1 - c.x - c.w }));
    } else {
      setFlipV((v) => !v);
      setCrop((c) => ({ ...c, y: 1 - c.y - c.h }));
    }
  };

  const resetAll = () => {
    setRot(0);
    setFlipH(false);
    setFlipV(false);
    setAspect("free");
    setCrop(FULL);
    setAdj(DEFAULT_ADJ);
  };

  /* ---------------------------------------------------------- crop dragging */
  const startDrag = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { mode, sx: e.clientX, sy: e.clientY, start: crop };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const W = disp.w;
    const H = disp.h;
    const onMove = (e) => {
      const d = drag.current;
      if (!d || !W) return;
      const dx = e.clientX - d.sx;
      const dy = e.clientY - d.sy;
      const s = { x: d.start.x * W, y: d.start.y * H, w: d.start.w * W, h: d.start.h * H };
      const m = d.mode;
      let left = s.x;
      let top = s.y;
      let right = s.x + s.w;
      let bottom = s.y + s.h;
      if (m === "move") {
        left = clamp(s.x + dx, 0, W - s.w);
        top = clamp(s.y + dy, 0, H - s.h);
        right = left + s.w;
        bottom = top + s.h;
      } else if (ratio && m.length === 2) {
        // Locked shape, corner drag: the opposite corner stays put and the box
        // follows whichever way the pointer moved further.
        const ax = m.includes("w") ? right : left;
        const ay = m.includes("n") ? bottom : top;
        const px = (m.includes("w") ? s.x : right) + dx;
        const py = (m.includes("n") ? s.y : bottom) + dy;
        let w = Math.max(Math.abs(px - ax), Math.abs(py - ay) * ratio);
        const maxW = Math.min(m.includes("w") ? ax : W - ax, (m.includes("n") ? ay : H - ay) * ratio);
        w = clamp(w, Math.min(maxW, Math.max(MIN_CROP, MIN_CROP * ratio)), maxW);
        const h = w / ratio;
        left = m.includes("w") ? ax - w : ax;
        top = m.includes("n") ? ay - h : ay;
        right = left + w;
        bottom = top + h;
      } else {
        if (m.includes("w")) left = clamp(s.x + dx, 0, right - MIN_CROP);
        if (m.includes("e")) right = clamp(right + dx, left + MIN_CROP, W);
        if (m.includes("n")) top = clamp(s.y + dy, 0, bottom - MIN_CROP);
        if (m.includes("s")) bottom = clamp(bottom + dy, top + MIN_CROP, H);
      }
      setCrop({ x: left / W, y: top / H, w: (right - left) / W, h: (bottom - top) / H });
    };
    const onUp = () => {
      drag.current = null;
      setDragging(false);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, disp, ratio]);

  /* ------------------------------------------------------------- filters */
  // Small cropped thumbnail for the filter strip.
  const thumb = useMemo(() => {
    if (!oriented || tab !== "filters") return "";
    const sx = crop.x * oriented.width;
    const sy = crop.y * oriented.height;
    const sw = crop.w * oriented.width;
    const sh = crop.h * oriented.height;
    const side = Math.min(sw, sh);
    const c = document.createElement("canvas");
    c.width = c.height = 120;
    c.getContext("2d").drawImage(oriented, sx + (sw - side) / 2, sy + (sh - side) / 2, side, side, 0, 0, 120, 120);
    return c.toDataURL("image/jpeg", 0.8);
  }, [oriented, crop, tab]);

  const activeFilter = FILTERS.find((f) => sameAdj(adj, { ...DEFAULT_ADJ, ...f.v }))?.key;

  /* --------------------------------------------------------------- save */
  const untouched = rot === 0 && !flipH && !flipV && sameAdj(adj, DEFAULT_ADJ) &&
    crop.x < 0.001 && crop.y < 0.001 && crop.w > 0.999 && crop.h > 0.999;

  const save = async () => {
    if (!img || saving) return;
    if (untouched) {
      onSave(file);
      return;
    }
    setSaving(true);
    try {
      // Let the spinner paint before the (synchronous) pixel work.
      await new Promise((r) => setTimeout(r, 30));
      const full = drawOriented(img, rot, flipH, flipV, EXPORT_MAX);
      const cx = Math.round(crop.x * full.width);
      const cy = Math.round(crop.y * full.height);
      const cw = Math.max(1, Math.min(full.width - cx, Math.round(crop.w * full.width)));
      const ch = Math.max(1, Math.min(full.height - cy, Math.round(crop.h * full.height)));
      const out = document.createElement("canvas");
      out.width = cw;
      out.height = ch;
      const ctx = out.getContext("2d");
      ctx.drawImage(full, cx, cy, cw, ch, 0, 0, cw, ch);
      if (!sameAdj(adj, DEFAULT_ADJ)) {
        const data = ctx.getImageData(0, 0, cw, ch);
        applyAdjust(data.data, adj);
        ctx.putImageData(data, 0, 0);
      }
      // Keep PNG/WebP (screenshots, transparency); everything else is JPEG.
      let type = file.type === "image/png" || file.type === "image/webp" ? file.type : "image/jpeg";
      let blob = await toBlob(out, type, 0.92);
      if (blob && blob.size > MEDIA_LIMITS.image) {
        type = "image/jpeg";
        blob = await toBlob(out, type, 0.85);
      }
      if (!blob) throw new Error("encode failed");
      const ext = type.split("/")[1].replace("jpeg", "jpg");
      const base = (file.name || "photo").replace(/\.[^.]+$/, "");
      onSave(new File([blob], `${base}-edited.${ext}`, { type, lastModified: Date.now() }));
    } catch {
      setSaving(false);
    }
  };

  // Pixel size of what Save will produce (same maths as drawOriented at EXPORT_MAX).
  let outW = 0;
  let outH = 0;
  if (img) {
    const k = Math.min(1, EXPORT_MAX / Math.max(img.naturalWidth, img.naturalHeight));
    const swap = rot % 180 !== 0;
    outW = Math.round(crop.w * (swap ? img.naturalHeight : img.naturalWidth) * k);
    outH = Math.round(crop.h * (swap ? img.naturalWidth : img.naturalHeight) * k);
  }

  /* --------------------------------------------------------------- render */
  const TABS = [
    { key: "crop", label: "Crop & rotate", short: "Crop", icon: ICONS.crop },
    { key: "adjust", label: "Adjust", short: "Adjust", icon: ICONS.adjust },
    { key: "filters", label: "Filters", short: "Filters", icon: ICONS.filters },
  ];

  const corner = (pos) => {
    const v = pos.includes("n") ? "top-0 -translate-y-[3px]" : "bottom-0 translate-y-[3px]";
    const h = pos.includes("w") ? "left-0 -translate-x-[3px]" : "right-0 translate-x-[3px]";
    const b = `${pos.includes("n") ? "border-t-[3px]" : "border-b-[3px]"} ${pos.includes("w") ? "border-l-[3px]" : "border-r-[3px]"}`;
    const cur = pos === "nw" || pos === "se" ? "cursor-nwse-resize" : "cursor-nesw-resize";
    return (
      <span
        key={pos}
        onPointerDown={startDrag(pos)}
        className={`absolute ${v} ${h} ${cur} grid h-7 w-7 touch-none`}
        style={{ placeItems: `${pos.includes("n") ? "start" : "end"} ${pos.includes("w") ? "start" : "end"}` }}
      >
        <span className={`block h-[18px] w-[18px] border-white ${b} drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]`} />
      </span>
    );
  };
  const edge = (pos) => {
    const horiz = pos === "n" || pos === "s";
    const place = {
      n: "left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize",
      s: "left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 cursor-ns-resize",
      w: "top-1/2 left-0 -translate-y-1/2 -translate-x-1/2 cursor-ew-resize",
      e: "top-1/2 right-0 -translate-y-1/2 translate-x-1/2 cursor-ew-resize",
    }[pos];
    return (
      <span key={pos} onPointerDown={startDrag(pos)} className={`absolute ${place} grid touch-none place-items-center ${horiz ? "h-6 w-10" : "h-10 w-6"}`}>
        <span className={`block rounded-full bg-white shadow ${horiz ? "h-[4px] w-6" : "h-6 w-[4px]"}`} />
      </span>
    );
  };

  const toolBtn = (icon, label, onClick, active = false) => (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col items-center gap-1.5 rounded-2xl border px-2 py-2.5 text-[11px] font-semibold transition hover:-translate-y-0.5"
      style={
        active
          ? { borderColor: `${primary}66`, background: `${primary}14`, color: primary }
          : undefined
      }
    >
      <span className={active ? "" : "text-gray-600 group-hover:text-gray-900"}>
        <Icon d={icon} className="h-5 w-5" />
      </span>
      <span className={active ? "" : "text-gray-500 group-hover:text-gray-800"}>{label}</span>
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-stretch justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6">
      <div className="relative flex h-full w-full flex-col overflow-hidden bg-surface shadow-2xl sm:h-[min(760px,calc(100dvh-3rem))] sm:max-w-5xl sm:rounded-[26px] sm:ring-1 sm:ring-black/10">
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 z-10 h-[3px]"
          style={{ background: `linear-gradient(90deg, ${primary}, ${primary}4d 70%, transparent)` }}
        />
        {/* Header */}
        <div className="flex shrink-0 items-center gap-3 border-b border-gray-100 px-5 py-4">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl" style={{ background: `${primary}17`, color: primary }}>
            <Icon d={ICONS.edit} className="h-[19px] w-[19px]" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-[17px] font-bold tracking-[-0.01em] text-gray-900">Edit photo</h3>
            <p className="truncate text-[12.5px] text-gray-500">Crop, rotate and fine-tune before you post</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="grid h-9 w-9 place-items-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close editor"
          >
            <Icon d={ICONS.close} className="h-5 w-5" sw={2} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {/* Stage */}
          <div
            ref={stageRef}
            className="relative min-h-[260px] flex-1 select-none overflow-hidden"
            style={{ background: "radial-gradient(120% 90% at 50% 40%, #1d1d27 0%, #0b0b10 70%)" }}
          >
            {!img && (
              <div className="absolute inset-0 grid place-items-center">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
              </div>
            )}
            {img && disp.w > 0 && (
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ width: disp.w, height: disp.h }}
              >
                <canvas
                  ref={canvasRef}
                  className="block h-full w-full rounded-[3px] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]"
                  style={{ filter: cssFilter(adj) }}
                />
                {tab === "crop" ? (
                  <div
                    onPointerDown={startDrag("move")}
                    className="absolute cursor-move touch-none"
                    style={{
                      left: crop.x * disp.w,
                      top: crop.y * disp.h,
                      width: crop.w * disp.w,
                      height: crop.h * disp.h,
                      boxShadow: "0 0 0 9999px rgba(8,8,12,0.62)",
                      outline: "1px solid rgba(255,255,255,0.85)",
                    }}
                  >
                    {/* Rule-of-thirds grid — stronger while dragging. */}
                    <div className={`pointer-events-none absolute inset-0 transition-opacity ${dragging ? "opacity-100" : "opacity-45"}`}>
                      <span className="absolute inset-y-0 left-1/3 w-px bg-white/60" />
                      <span className="absolute inset-y-0 left-2/3 w-px bg-white/60" />
                      <span className="absolute inset-x-0 top-1/3 h-px bg-white/60" />
                      <span className="absolute inset-x-0 top-2/3 h-px bg-white/60" />
                    </div>
                    {["nw", "ne", "sw", "se"].map(corner)}
                    {!ratio && ["n", "s", "w", "e"].map(edge)}
                  </div>
                ) : (
                  // Outside the crop tab, show the result: dim what will be cut.
                  (crop.w < 0.999 || crop.h < 0.999) && (
                    <div
                      className="pointer-events-none absolute"
                      style={{
                        left: crop.x * disp.w,
                        top: crop.y * disp.h,
                        width: crop.w * disp.w,
                        height: crop.h * disp.h,
                        boxShadow: "0 0 0 9999px rgba(8,8,12,0.85)",
                      }}
                    />
                  )
                )}
              </div>
            )}
            {img && outW > 0 && (
              <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-[11px] font-semibold tabular-nums text-white/85 backdrop-blur">
                {outW} × {outH}
              </span>
            )}
          </div>

          {/* Controls */}
          <div className="flex shrink-0 flex-col border-t border-gray-100 md:w-[320px] md:border-l md:border-t-0">
            <div className="p-4 pb-0">
              <div className="grid grid-cols-3 gap-1 rounded-2xl bg-gray-100 p-1">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`flex items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-[12.5px] font-semibold transition ${
                      tab === t.key ? "bg-surface shadow-sm" : "text-gray-500 hover:text-gray-800"
                    }`}
                    style={tab === t.key ? { color: primary } : undefined}
                  >
                    <Icon d={t.icon} className="h-4 w-4" />
                    {t.short}
                  </button>
                ))}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {tab === "crop" && (
                <div className="space-y-5">
                  <div>
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">Aspect ratio</p>
                    <div className="flex flex-wrap gap-2">
                      {ASPECTS.map((a) => {
                        const on = aspect === a.key;
                        return (
                          <button
                            key={a.key}
                            type="button"
                            onClick={() => pickAspect(a.key)}
                            className={`rounded-full border px-3.5 py-1.5 text-[12.5px] font-semibold transition ${
                              on ? "" : "border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900"
                            }`}
                            style={on ? { borderColor: primary, background: `${primary}14`, color: primary } : undefined}
                          >
                            {a.label}
                            {a.ratio && <span className="ml-1 opacity-60">{a.key}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">Rotate & flip</p>
                    <div className="grid grid-cols-4 gap-2">
                      {toolBtn(ICONS.rotL, "Left", () => rotate(-1))}
                      {toolBtn(ICONS.rotR, "Right", () => rotate(1))}
                      {toolBtn(ICONS.flipH, "Mirror", () => flip("h"), flipH)}
                      {toolBtn(ICONS.flipV, "Flip", () => flip("v"), flipV)}
                    </div>
                  </div>
                  <p className="rounded-xl bg-gray-50 px-3 py-2.5 text-[12px] leading-relaxed text-gray-500">
                    Drag the frame to move it, and its corners to resize.
                  </p>
                </div>
              )}

              {tab === "adjust" && (
                <div className="space-y-5">
                  {SLIDERS.map((sl) => {
                    const def = DEFAULT_ADJ[sl.key];
                    const v = adj[sl.key];
                    const pct = def === 0 ? Math.round(v * 100) : Math.round(((v - def) / ((sl.max - sl.min) / 2)) * 100);
                    return (
                      <div key={sl.key}>
                        <div className="mb-1.5 flex items-center justify-between">
                          <span className="text-[13px] font-semibold text-gray-700">{sl.label}</span>
                          <span
                            className="min-w-[44px] rounded-full px-2 py-0.5 text-center text-[11.5px] font-bold tabular-nums"
                            style={pct !== 0 ? { background: `${primary}14`, color: primary } : undefined}
                          >
                            {pct > 0 ? `+${pct}` : pct}
                          </span>
                        </div>
                        <input
                          type="range"
                          min={sl.min}
                          max={sl.max}
                          step={sl.step}
                          value={v}
                          onChange={(e) => setAdj((a) => ({ ...a, [sl.key]: Number(e.target.value) }))}
                          onDoubleClick={() => setAdj((a) => ({ ...a, [sl.key]: def }))}
                          className="w-full cursor-pointer"
                          style={{ accentColor: primary }}
                          aria-label={sl.label}
                        />
                      </div>
                    );
                  })}
                  <p className="text-[12px] text-gray-400">Double-click a slider to reset it.</p>
                </div>
              )}

              {tab === "filters" && (
                <div className="grid grid-cols-4 gap-2.5 md:grid-cols-3">
                  {FILTERS.map((f) => {
                    const on = activeFilter === f.key;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setAdj({ ...DEFAULT_ADJ, ...f.v })}
                        className="group flex flex-col items-center gap-1.5"
                      >
                        <span
                          className="block aspect-square w-full overflow-hidden rounded-2xl transition group-hover:-translate-y-0.5"
                          style={on ? { boxShadow: `0 0 0 2px rgb(var(--c-surface)), 0 0 0 4px ${primary}` } : undefined}
                        >
                          {thumb && (
                            <img src={thumb} alt="" className="h-full w-full object-cover" style={{ filter: cssFilter({ ...DEFAULT_ADJ, ...f.v }) }} />
                          )}
                        </span>
                        <span className={`text-[11.5px] font-semibold ${on ? "" : "text-gray-500"}`} style={on ? { color: primary } : undefined}>
                          {f.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex shrink-0 items-center gap-2 border-t border-gray-100 bg-gray-50/60 px-4 py-3">
              <button
                type="button"
                onClick={resetAll}
                disabled={untouched}
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-semibold text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <Icon d={ICONS.reset} className="h-4 w-4" />
                Reset
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={onCancel}
                className="rounded-xl border border-gray-200 px-4 py-2 text-[13px] font-semibold text-gray-600 transition hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={!img || saving}
                className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-bold transition hover:brightness-[1.06] disabled:opacity-50"
                style={{
                  backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0) 58%)",
                  backgroundColor: primary,
                  color: "var(--brand-btn-text, #fff)",
                  boxShadow: `0 10px 22px -12px ${primary}`,
                }}
              >
                {saving ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <Icon d={ICONS.check} className="h-4 w-4" sw={2.4} />
                )}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Whether a composer media entry can go through the editor: only images picked
 * from this device. A GIF would lose its animation, and images already on a
 * post/comment being edited are remote URLs.
 */
export const isEditableImage = (m) => !!m?.file && !m.isGif && m.file.type !== "image/gif";

/**
 * The "Edit" chip on a composer thumbnail. `compact` is the icon-only round
 * version for the small (64px) comment/reply thumbnails.
 */
export function EditPhotoButton({ onClick, compact = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Edit photo"
      title="Edit photo"
      className={`absolute bottom-1 left-1 inline-flex items-center justify-center gap-1 rounded-full bg-black/55 font-semibold text-white shadow-sm ring-1 ring-white/20 backdrop-blur-md transition hover:bg-black/75 ${
        compact ? "h-5 w-5" : "bottom-1.5 left-1.5 px-2 py-[3px] text-[10.5px]"
      }`}
    >
      <Icon d={ICONS.edit} className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} sw={2.2} />
      {!compact && "Edit"}
    </button>
  );
}
